import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { ROLE_HOME, ROLE_LABELS, ROLES } from "@/lib/roles";
import HoneyAcornMark from "@/brand/HoneyAcornMark";

export default function Login() {
  const {
    signIn,
    resetPasswordForEmail,
    demoSignIn,
    isSupabaseConfigured,
    isDemoModeEnabled,
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from;

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [resetting, setResetting] = useState(false);

  const goHome = (role) => {
    const target = from || ROLE_HOME[role] || "/";
    navigate(target, { replace: true });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await signIn(email, password);
      toast.success("Welcome back");
      // AuthContext will load profile; role-based redirect happens on next render via <Navigate />.
      navigate(from || "/", { replace: true });
    } catch (err) {
      toast.error(err.message || "Sign in failed");
    } finally {
      setSubmitting(false);
    }
  };

  const onDemo = (role) => {
    demoSignIn(role);
    toast.success(`Signed in as ${ROLE_LABELS[role]} (demo)`);
    goHome(role);
  };

  const onResetPassword = async () => {
    const normalizedEmail = email.trim();
    if (!normalizedEmail) {
      toast.error("Enter your email address first.");
      return;
    }
    setResetting(true);
    try {
      await resetPasswordForEmail(normalizedEmail);
      toast.success("If that email has an account, we sent a recovery link.");
    } catch (err) {
      toast.error(err.message || "Could not send a recovery link");
    } finally {
      setResetting(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-background"
      data-testid="login-page"
    >
      <div className="mx-auto grid min-h-screen w-full max-w-6xl grid-cols-1 lg:grid-cols-2">
        {/* Left — brand panel */}
        <div className="relative hidden overflow-hidden bg-neutral-100 lg:block">
          <img
            src="https://images.pexels.com/photos/8535158/pexels-photo-8535158.jpeg"
            alt="Modern sunny daycare interior"
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-tr from-neutral-900/65 via-neutral-900/20 to-transparent" />
          <div className="relative z-10 flex h-full flex-col justify-between p-12 text-white">
            <div className="flex items-center gap-2.5">
              <HoneyAcornMark size={30} />
              <div>
                <div className="font-heading text-lg font-bold tracking-tight text-white">
                  HoneyAcorn
                </div>
                <div className="font-mono text-[8.5px] uppercase tracking-[0.18em] text-white/60">
                  Childcare, connected
                </div>
              </div>
            </div>
            <div className="max-w-md">
              <h1 className="font-heading text-4xl font-bold leading-[1.05] tracking-tight sm:text-5xl">
                The operating platform for childcare, and the families it serves.
              </h1>
              <p className="mt-5 text-base leading-relaxed text-white/80">
                Enrollment, attendance, billing, daily updates, and family
                messaging, on one secure, Supabase-powered backbone.
              </p>
            </div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/60">
              Childcare, connected
            </div>
          </div>
        </div>

        {/* Right — form */}
        <div className="flex items-center justify-center px-6 py-12 sm:px-12">
          <div className="w-full max-w-md">
            <div className="mb-8">
              <div className="mb-4 flex items-center gap-2.5 lg:hidden">
                <HoneyAcornMark size={30} />
                <div>
                  <div className="font-heading text-lg font-bold tracking-tight text-neutral-900">
                    HoneyAcorn
                  </div>
                  <div className="font-mono text-[8.5px] uppercase tracking-[0.18em] text-neutral-500">
                    Childcare, connected
                  </div>
                </div>
              </div>
              <h2 className="font-heading text-3xl font-semibold tracking-tight text-neutral-900 sm:text-4xl">
                Welcome back
              </h2>
              <p className="mt-2 text-base text-neutral-500">
                Sign in to access your dashboard.
              </p>
            </div>

            {!isSupabaseConfigured && !isDemoModeEnabled && (
              <div
                data-testid="supabase-not-configured-banner"
                className="mb-6 rounded-2xl border border-primary/30 bg-primary/[0.06] p-4"
              >
                <div className="flex items-start gap-3">
                  <Sparkle
                    size={18}
                    weight="duotone"
                    className="mt-0.5 text-primary-hover"
                  />
                  <div className="text-sm leading-relaxed text-neutral-800">
                    <p className="font-semibold text-neutral-900">
                      Live authentication is not configured
                    </p>
                    <p className="mt-1 text-neutral-700">
                      Configure{" "}
                      <code className="rounded bg-white px-1 py-0.5 text-[11px]">
                        REACT_APP_SUPABASE_URL
                      </code>{" "}
                      and{" "}
                      <code className="rounded bg-white px-1 py-0.5 text-[11px]">
                        REACT_APP_SUPABASE_PUBLISHABLE_KEY
                      </code>{" "}
                      (or the legacy anon key) to enable real auth.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <form
              onSubmit={onSubmit}
              className="space-y-4"
              data-testid="login-form"
            >
              <div>
                <label htmlFor="login-email" className="mb-1.5 block text-sm font-medium text-neutral-800">
                  Email
                </label>
                <input
                  id="login-email"
                  name="email"
                  data-testid="login-email-input"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  autoComplete="username"
                  className="w-full rounded-xl border border-neutral-200 bg-white/70 px-4 py-3 text-neutral-900 placeholder:text-neutral-400 shadow-sm transition-all focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
              </div>
              <div>
                <div className="mb-1.5 flex items-center justify-between gap-3">
                  <label htmlFor="login-password" className="block text-sm font-medium text-neutral-800">
                    Password
                  </label>
                  <button
                    type="button"
                    data-testid="login-forgot-password"
                    onClick={onResetPassword}
                    disabled={!isSupabaseConfigured || resetting}
                    className="inline-flex min-h-11 items-center px-2 -mx-2 text-xs font-semibold text-primary-hover hover:underline disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {resetting ? "Sending…" : "Forgot password?"}
                  </button>
                </div>
                <input
                  id="login-password"
                  name="password"
                  data-testid="login-password-input"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  className="w-full rounded-xl border border-neutral-200 bg-white/70 px-4 py-3 text-neutral-900 placeholder:text-neutral-400 shadow-sm transition-all focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
              </div>
              <button
                data-testid="login-submit-button"
                type="submit"
                disabled={submitting || !isSupabaseConfigured}
                className="w-full rounded-full bg-primary px-6 py-3 text-sm font-semibold text-white shadow-sm transition-all hover:bg-primary-hover active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50"
              >
                {submitting ? "Signing in…" : "Sign in"}
              </button>
              <p className="text-center text-sm text-neutral-500">
                New here?{" "}
                <Link
                  to="/signup"
                  data-testid="login-to-signup-link"
                  className="font-medium text-primary-hover hover:underline"
                >
                  Create an account
                </Link>
              </p>
            </form>

            {isDemoModeEnabled && !isSupabaseConfigured && (
              <div className="mt-8">
                <div className="overline mb-3 text-neutral-500">
                  Preview a role
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {Object.values(ROLES)
                    // Platform control is provisioned with a one-time server
                    // bootstrap, never through a review/demo identity.
                    .filter((r) => r !== ROLES.PLATFORM_ADMIN)
                    .map((r) => (
                    <button
                      key={r}
                      data-testid={`demo-signin-${r}`}
                      onClick={() => onDemo(r)}
                      className="rounded-full border border-neutral-200 bg-white px-4 py-2.5 text-sm font-medium text-neutral-800 transition-all hover:border-primary hover:bg-primary/5 hover:text-primary-hover active:scale-[0.98]"
                    >
                      {ROLE_LABELS[r]}
                    </button>
                    ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
